import React from 'react';
import { IconLogout } from '@tabler/icons-react';
import type { Rack, Recipe } from '@recipe-rack/shared';

import { IconButton, RRLogo } from '../../design/components';
import PasteBar from '../Library/PasteBar';
import { auth } from '../Recipe/Firebase/InitialiseFirebase';
import { signOutUser } from '../Auth/authHelpers';

export interface SidebarProps {
  userId: string;
  userName?: string | null;
  racks: Rack[];
  recipes: Recipe[];
  selectedRackId: string | null;
  onSelectRack: (rackId: string | null) => void;
  onManageRacks: () => void;
}

/**
 * Left sidebar (>=880px): wordmark, paste bar, rack filter list, user pill.
 * Below 880px the parent `<Shell>` hides this and renders a thin header instead.
 */
const Sidebar: React.FC<SidebarProps> = ({
  userId,
  userName,
  racks,
  recipes,
  selectedRackId,
  onSelectRack,
  onManageRacks,
}) => {
  const allCount = recipes.filter((r) => r.version !== 0).length;
  const countFor = (rackId: string) =>
    recipes.filter((r) => r.version !== 0 && (r.rackIds ?? []).includes(rackId)).length;
  const unfiledCount = recipes.filter(
    (r) => r.version !== 0 && (!r.rackIds || r.rackIds.length === 0)
  ).length;

  const initial = (userName ?? 'You').trim().charAt(0).toUpperCase() || 'Y';
  const subtitle = `${allCount} recipe${allCount === 1 ? '' : 's'} · ${racks.length} rack${
    racks.length === 1 ? '' : 's'
  }`;

  return (
    <aside className="rr-sidebar" aria-label="Library navigation">
      <div className="rr-sidebar-head">
        <a href="/" className="rr-sidebar-brand" aria-label="Recipe Rack home">
          <span className="rr-sidebar-brand-mark">
            <RRLogo shelf="mixed" size={28} />
          </span>
          <span className="rr-sidebar-brand-name">Recipe Rack</span>
        </a>
      </div>

      <div className="rr-sidebar-paste">
        <PasteBar userId={userId} />
      </div>

      <div className="rr-sidebar-section">
        <span>Library</span>
      </div>
      <div className="rr-sidebar-nav" style={{ flex: '0 0 auto' }}>
        <button
          type="button"
          className={['rr-sidebar-row', selectedRackId === null ? 'is-selected' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => onSelectRack(null)}
        >
          <span className="rr-sidebar-row-name">All recipes</span>
          <span className="rr-sidebar-row-count">{allCount}</span>
        </button>
      </div>

      <div className="rr-sidebar-section">
        <span>Racks</span>
        <button
          type="button"
          className="rr-sidebar-section-btn"
          aria-label="Manage racks"
          title="Manage racks"
          onClick={onManageRacks}
        >
          +
        </button>
      </div>
      <div className="rr-sidebar-nav">
        {racks.map((r) => (
          <button
            type="button"
            key={r.rackId}
            className={['rr-sidebar-row', selectedRackId === r.rackId ? 'is-selected' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onSelectRack(r.rackId)}
          >
            <span className="rr-sidebar-row-dot" aria-hidden="true" />
            <span className="rr-sidebar-row-name">{r.name}</span>
            <span className="rr-sidebar-row-count">{countFor(r.rackId)}</span>
          </button>
        ))}
        {unfiledCount > 0 && (
          <button
            type="button"
            className={['rr-sidebar-row', selectedRackId === '__unfiled' ? 'is-selected' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onSelectRack('__unfiled')}
          >
            <span
              className="rr-sidebar-row-dot"
              style={{ background: 'var(--ink-faint)' }}
              aria-hidden="true"
            />
            <span className="rr-sidebar-row-name">Unfiled</span>
            <span className="rr-sidebar-row-count">{unfiledCount}</span>
          </button>
        )}
      </div>

      <div className="rr-sidebar-foot">
        <div className="rr-sidebar-foot-avatar" aria-hidden="true">
          {initial}
        </div>
        <div className="rr-sidebar-foot-meta">
          <b>{userName ?? 'Your shelf'}</b>
          <span>{subtitle}</span>
        </div>
        <IconButton
          icon={<IconLogout size={16} />}
          aria-label="Sign out"
          size="sm"
          onClick={() => {
            signOutUser(auth).catch(() => {
              /* swallow — App's auth listener will redraw regardless */
            });
          }}
        />
      </div>
    </aside>
  );
};

export default Sidebar;
